import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/apiFetch";
import { usePermissions } from "../hooks/usePermissions";
import CostSplitGroups from "./CostSplitGroups";
import PostJournalEntry from "./PostJournalEntry";

const C = {
  bg: "#0d0f12",
  surface: "#13161b",
  border: "#1e2329",
  text: "#e8ecf0",
  muted: "#8a919c",
  green: "#22c55e",
  greenDim: "rgba(34,197,94,0.08)",
  red: "#ef4444",
  redDim: "rgba(239,68,68,0.08)",
  yellow: "#f59e0b",
  blue: "#60a5fa",
};

function StatusCard({ label, ok, detail }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
      padding: "14px 18px", minWidth: 180, flex: "1 1 160px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <div style={{
          width: 8, height: 8, borderRadius: "50%",
          background: ok == null ? C.muted : ok ? C.green : C.red,
        }} />
        <div style={{ fontSize: 14, fontWeight: 600 }}>{label}</div>
      </div>
      <div style={{ fontSize: 12, color: ok == null ? C.muted : ok ? C.green : C.red }}>
        {ok == null ? "Checking…" : ok ? "Connected" : "Disconnected"}
      </div>
      {detail && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{detail}</div>}
    </div>
  );
}

function SyncRow({ label, description, loading, result, onSync }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{label}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{description}</div>
        </div>
        <button
          onClick={onSync}
          disabled={loading}
          style={{
            padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.border}`,
            background: "transparent", color: C.muted, fontSize: 13, fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.5 : 1,
            whiteSpace: "nowrap",
          }}
        >
          {loading ? "Running…" : "Run Now"}
        </button>
      </div>
      {result && (
        <div style={{
          marginTop: 10, padding: "8px 12px", borderRadius: 6, fontSize: 12,
          background: result.ok ? C.greenDim : C.redDim,
          color: result.ok ? C.green : C.red,
          border: `1px solid ${result.ok ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"}`,
          wordBreak: "break-word",
        }}>
          {result.ok
            ? JSON.stringify(result.data).slice(0, 300)
            : result.error}
        </div>
      )}
    </div>
  );
}

export default function AdminDiagnostics() {
  const perms = usePermissions() || {};
  const { isAdmin, isOwner, realIsOwner } = perms;

  const [jobberStatus, setJobberStatus] = useState(null);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [qbStatus, setQbStatus] = useState(null);
  const [syncing, setSyncing] = useState({});
  const [results, setResults] = useState({});

  // Profit-bonus settings — four keys in app_settings:
  //   profit_bonus_lead_pct, profit_bonus_crew_pct,
  //   profit_bonus_revenue_threshold, profit_bonus_margin_threshold
  const BONUS_KEYS = [
    { key: "profit_bonus_lead_pct",          label: "Lead Tech Bonus %",     suffix: "%", default: 10,   min: 0, max: 100, step: 0.5,
      hint: "% of (profit − revenue × 50%) paid to whoever leads each qualifying job." },
    { key: "profit_bonus_crew_pct",          label: "Crew Bonus % (pool)",   suffix: "%", default: 5,    min: 0, max: 100, step: 0.5,
      hint: "% of excess profit paid to the crew pool (split among everyone who logged hours, plus the admin)." },
    { key: "profit_bonus_revenue_threshold", label: "Revenue Threshold",     suffix: "$", default: 5500, min: 0, max: 1000000, step: 100,
      hint: "Job revenue must exceed this for any bonus to apply. Changes here re-apply to all unpaid bonuses." },
    { key: "profit_bonus_margin_threshold",  label: "Margin Threshold",      suffix: "%", default: 50,   min: 0, max: 100, step: 1,
      hint: "Profit margin must exceed this %. Used as both the eligibility floor and the 50% line subtracted from profit." },
  ];
  const [bonusSettings, setBonusSettings] = useState({});       // current input values
  const [bonusSettingsSaved, setBonusSettingsSaved] = useState({}); // persisted DB values
  const [bonusSettingsSaving, setBonusSettingsSaving] = useState({});
  const [bonusSettingsMsg, setBonusSettingsMsg] = useState({});

  // QuickBooks auto-push toggle. Backed by app_settings.auto_qb_push_enabled
  // (jsonb boolean). Default off — admin must explicitly opt in.
  const [autoPushEnabled, setAutoPushEnabled] = useState(false);
  const [autoPushSaving, setAutoPushSaving] = useState(false);
  const [autoPushMsg, setAutoPushMsg] = useState(null);

  // QuickBooks cache (qb_vendors / qb_accounts) freshness. The auto-push gate
  // checks vendorExistsForSupplier against qb_vendors — if the cache is stale
  // and missing a vendor that exists in QB, every auto-push for that supplier
  // silently fails. Surfacing the last-sync age + manual resync button so
  // operator can spot drift before it bites.
  const [qbCacheStatus, setQbCacheStatus] = useState(null);    // { vendors, accounts, last_synced_at }
  const [qbCacheSyncing, setQbCacheSyncing] = useState(false);
  const [qbCacheMsg, setQbCacheMsg] = useState(null);

  // Claude API spend telemetry. Pulled from /api/claude-spend which aggregates
  // claude_call_log. Lets us see per-caller token + estimated $ for the chosen
  // window — figure out which endpoint is eating the Anthropic bill.
  const [spendDays, setSpendDays] = useState(7);
  const [spendLoading, setSpendLoading] = useState(false);
  const [spendData, setSpendData] = useState(null);
  const [spendError, setSpendError] = useState(null);

  // Credit-note audit. Scans pending_invoices for the chosen supplier
  // substring (default "city"), downloads each PDF, parses with pdf-parse,
  // and surfaces rows whose PDF header contains "Credit Note - Do Not Pay"
  // (or similar) even though the row was saved with a positive total.
  // Admin can then bulk-flip the sign + flag as a credit memo.
  const [creditAuditSupplier, setCreditAuditSupplier] = useState("city");
  const [creditAuditLoading, setCreditAuditLoading] = useState(false);
  const [creditAuditFlipping, setCreditAuditFlipping] = useState(false);
  const [creditAuditData, setCreditAuditData] = useState(null);
  const [creditAuditError, setCreditAuditError] = useState(null);
  const [creditAuditSelected, setCreditAuditSelected] = useState(new Set());
  // Undo-mode state — surfaces rows already flipped so admin can spot-check
  // and reverse any false positives (e.g. SAL/212570 caught by the unscoped
  // multi-section PDF check).
  const [unflipMode, setUnflipMode] = useState(false);
  const [unflipLoading, setUnflipLoading] = useState(false);
  const [unflipData, setUnflipData] = useState(null);
  const [unflipSelected, setUnflipSelected] = useState(new Set());
  const [unflipRunning, setUnflipRunning] = useState(false);

  useEffect(() => {
    checkStatuses();
    loadBonusSettings();
    loadAutoPushSetting();
    loadQbCacheStatus();
    loadClaudeSpend(7);
  }, []);

  async function loadClaudeSpend(days) {
    setSpendLoading(true);
    setSpendError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not signed in");
      const r = await fetch(`/api/claude-spend?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || `HTTP ${r.status}`);
      setSpendData(data);
    } catch (e) {
      setSpendError(e.message || "Failed to load spend");
      setSpendData(null);
    } finally {
      setSpendLoading(false);
    }
  }

  async function runCreditNoteAudit() {
    setCreditAuditLoading(true);
    setCreditAuditError(null);
    setCreditAuditSelected(new Set());
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not signed in");
      const r = await fetch(
        `/api/credit-note-audit?action=scan&supplier=${encodeURIComponent(creditAuditSupplier)}&limit=200`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || `HTTP ${r.status}`);
      setCreditAuditData(data);
      // Pre-select all flagged credit notes so admin can review then click flip.
      const flaggedIds = (data.results || [])
        .filter((rr) => rr.isCreditNote)
        .map((rr) => rr.id);
      setCreditAuditSelected(new Set(flaggedIds));
    } catch (e) {
      setCreditAuditError(e.message || "Audit failed");
      setCreditAuditData(null);
    } finally {
      setCreditAuditLoading(false);
    }
  }

  async function flipSelectedCreditNotes() {
    if (creditAuditSelected.size === 0) return;
    const count = creditAuditSelected.size;
    if (!window.confirm(`Flip ${count} pending_invoice(s) to credit memo (negative totals)?\n\nRows already pushed to Jobber/QB will get an admin_notes flag — you'll need to push a corrective adjustment manually.`)) return;
    setCreditAuditFlipping(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not signed in");
      const r = await fetch("/api/credit-note-audit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "flip", invoiceIds: [...creditAuditSelected] }),
      });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || `HTTP ${r.status}`);
      const wasPushedCount = (data.results || []).filter((rr) => rr.wasPushed).length;
      setCreditAuditError(
        `Flipped ${data.flipped}/${data.processed}.` +
        (wasPushedCount > 0 ? ` ⚠ ${wasPushedCount} were already pushed — see admin_notes on those rows for next steps.` : "")
      );
      // Re-run scan so suspect list shrinks.
      await runCreditNoteAudit();
    } catch (e) {
      setCreditAuditError("Flip failed: " + (e.message || "unknown"));
    } finally {
      setCreditAuditFlipping(false);
    }
  }

  function toggleCreditAuditRow(id) {
    setCreditAuditSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function loadFlippedRows() {
    setUnflipLoading(true);
    setCreditAuditError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not signed in");
      const r = await fetch("/api/credit-note-audit?action=listFlipped", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || `HTTP ${r.status}`);
      setUnflipData(data);
      setUnflipSelected(new Set());
    } catch (e) {
      setCreditAuditError("Load flipped rows failed: " + (e.message || "unknown"));
      setUnflipData(null);
    } finally {
      setUnflipLoading(false);
    }
  }

  async function unflipSelectedRows() {
    if (unflipSelected.size === 0) return;
    if (!window.confirm(`Un-flip ${unflipSelected.size} row(s) back to a regular invoice?`)) return;
    setUnflipRunning(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not signed in");
      const r = await fetch("/api/credit-note-audit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "unflip", invoiceIds: [...unflipSelected] }),
      });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || `HTTP ${r.status}`);
      setCreditAuditError(`Un-flipped ${data.unflipped}/${data.processed}.`);
      await loadFlippedRows();
    } catch (e) {
      setCreditAuditError("Un-flip failed: " + (e.message || "unknown"));
    } finally {
      setUnflipRunning(false);
    }
  }

  function toggleUnflipRow(id) {
    setUnflipSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function loadQbCacheStatus() {
    try {
      const [{ data: vendors }, { data: accounts }] = await Promise.all([
        supabase.from("qb_vendors").select("synced_at", { count: "exact", head: false }).order("synced_at", { ascending: false }).limit(1),
        supabase.from("qb_accounts").select("synced_at", { count: "exact", head: false }).order("synced_at", { ascending: false }).limit(1),
      ]);
      // Count via a separate head=true query so we get { count } back.
      const [{ count: vCount }, { count: aCount }] = await Promise.all([
        supabase.from("qb_vendors").select("*", { count: "exact", head: true }),
        supabase.from("qb_accounts").select("*", { count: "exact", head: true }),
      ]);
      setQbCacheStatus({
        vendors:         vCount || 0,
        accounts:        aCount || 0,
        last_synced_at:  vendors?.[0]?.synced_at || accounts?.[0]?.synced_at || null,
      });
    } catch {
      setQbCacheStatus({ vendors: 0, accounts: 0, last_synced_at: null });
    }
  }

  async function resyncQbCache() {
    if (qbCacheSyncing) return;
    setQbCacheSyncing(true);
    setQbCacheMsg(null);
    try {
      const r = await apiFetch("/api/quickbooks-accounts?action=sync");
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "sync failed");
      setQbCacheMsg({
        ok: true,
        text: `Synced ${data.vendors_synced} vendors, ${data.accounts_synced} accounts.`,
      });
      await loadQbCacheStatus();
    } catch (e) {
      setQbCacheMsg({ ok: false, text: "Sync failed: " + (e.message || "unknown error") });
    }
    setQbCacheSyncing(false);
    setTimeout(() => setQbCacheMsg(null), 6000);
  }

  async function loadAutoPushSetting() {
    const { data } = await supabase
      .from("app_settings")
      .select("value")
      .eq("key", "auto_qb_push_enabled")
      .maybeSingle();
    const v = data?.value;
    setAutoPushEnabled(v === true || v === "true" || v === 1);
  }

  async function toggleAutoPush(next) {
    setAutoPushSaving(true);
    setAutoPushMsg(null);
    try {
      const { error } = await supabase
        .from("app_settings")
        .upsert(
          { key: "auto_qb_push_enabled", value: next, updated_at: new Date().toISOString() },
          { onConflict: "key" }
        );
      if (error) throw error;
      setAutoPushEnabled(next);
      setAutoPushMsg({
        ok: true,
        text: next
          ? "Auto-push ON. Known suppliers (electrical wholesalers, fuel cards, TSBC permits) with a matched QB vendor will publish automatically."
          : "Auto-push OFF. New invoices will land in Expenses for manual review.",
      });
    } catch (e) {
      setAutoPushMsg({ ok: false, text: e.message || "Save failed" });
    } finally {
      setAutoPushSaving(false);
      setTimeout(() => setAutoPushMsg(null), 6000);
    }
  }

  async function loadBonusSettings() {
    const keys = BONUS_KEYS.map(k => k.key);
    const { data } = await supabase
      .from("app_settings")
      .select("key,value")
      .in("key", keys);
    const map = {};
    for (const k of BONUS_KEYS) {
      const row = (data || []).find(d => d.key === k.key);
      const v = row?.value;
      const parsed = typeof v === "number" ? v : typeof v === "string" ? Number(v) : null;
      map[k.key] = String(parsed != null && !isNaN(parsed) ? parsed : k.default);
    }
    setBonusSettings({ ...map });
    setBonusSettingsSaved({ ...map });
  }

  async function saveBonusSetting(k) {
    const meta = BONUS_KEYS.find(x => x.key === k);
    const n = Number(bonusSettings[k]);
    if (isNaN(n) || n < meta.min || n > meta.max) {
      setBonusSettingsMsg(m => ({ ...m, [k]: { ok: false, text: `Must be between ${meta.min} and ${meta.max}.` } }));
      return;
    }
    setBonusSettingsSaving(s => ({ ...s, [k]: true }));
    setBonusSettingsMsg(m => ({ ...m, [k]: null }));
    try {
      const { error } = await supabase
        .from("app_settings")
        .upsert({ key: k, value: n, updated_at: new Date().toISOString() }, { onConflict: "key" });
      if (error) throw error;
      setBonusSettingsSaved(s => ({ ...s, [k]: String(n) }));
      setBonusSettingsMsg(m => ({ ...m, [k]: { ok: true, text: `Saved — ${meta.label} is now ${meta.suffix === "$" ? "$" : ""}${n}${meta.suffix === "%" ? "%" : ""}.` } }));
    } catch (e) {
      setBonusSettingsMsg(m => ({ ...m, [k]: { ok: false, text: e.message || "Save failed" } }));
    } finally {
      setBonusSettingsSaving(s => ({ ...s, [k]: false }));
      setTimeout(() => setBonusSettingsMsg(m => ({ ...m, [k]: null })), 4000);
    }
  }

  async function checkStatuses() {
    setJobberStatus(null);
    setGmailStatus(null);
    setQbStatus(null);

    const [jobberRes, gmailRes, qbRes] = await Promise.allSettled([
      apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "status" }),
      }).then(r => r.json()),
      fetch("/api/gmail-auth?action=status").then(r => r.json()),
      fetch("/api/quickbooks-auth?action=status").then(r => r.json()),
    ]);

    setJobberStatus(jobberRes.status === "fulfilled" ? jobberRes.value : { error: true });
    setGmailStatus(gmailRes.status === "fulfilled" ? gmailRes.value : { error: true });
    setQbStatus(qbRes.status === "fulfilled" ? qbRes.value : { error: true });
  }

  async function runSync(key, fn) {
    setSyncing(s => ({ ...s, [key]: true }));
    setResults(r => ({ ...r, [key]: null }));
    try {
      const data = await fn();
      setResults(r => ({ ...r, [key]: { ok: data.ok !== false, data } }));
    } catch (e) {
      setResults(r => ({ ...r, [key]: { ok: false, error: e.message } }));
    } finally {
      setSyncing(s => ({ ...s, [key]: false }));
    }
  }

  const canAccess = isAdmin || isOwner || realIsOwner;
  if (!canAccess) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, color: C.muted, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
        Access denied — admin only.
      </div>
    );
  }

  const syncs = [
    {
      key: "jobber_crew",
      label: "Sync Jobber (Crew Data)",
      description: "Pull timesheets, jobs, visits from the last 30 days. Run by daily cron — use this only for ad-hoc refresh.",
      fn: () => apiFetch("/api/jobber-sync", { method: "POST" }).then(r => r.json()),
    },
    {
      key: "jobber_backfill_180",
      label: "Backfill Jobber (180 days)",
      description: "Re-pull the last 180 days of timesheets, visits, jobs, and invoices. Use after a long sync gap or to refresh historical data.",
      fn: () => apiFetch("/api/jobber-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days: 180 }),
      }).then(r => r.json()),
    },
    {
      key: "jobber_backfill_full",
      label: "Backfill Jobber (Full History)",
      description: "Re-pull EVERYTHING from Jobber (no date filter). Slow — only run when bootstrapping or after a major schema change.",
      fn: () => apiFetch("/api/jobber-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "fullSync" }),
      }).then(r => r.json()),
    },
    {
      key: "jobber_expenses",
      label: "Sync Jobber Expenses",
      description: "Pull tech-entered expenses from Jobber into pending invoices",
      fn: () => apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "syncJobberExpenses" }),
      }).then(r => r.json()),
    },
    {
      key: "reviews",
      label: "Sync Google Reviews",
      description: "Fetch latest Google reviews via Outscraper (pending GBP API approval)",
      fn: () => apiFetch("/api/google-reviews-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "sync" }),
      }).then(r => r.json()),
    },
    {
      key: "review_emails",
      label: "Sync Review Emails",
      description: "Parse Google review-notification emails into the reviews feed (no API needed)",
      fn: () => apiFetch("/api/review-emails", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "sync" }),
      }).then(r => r.json()),
    },
    {
      key: "gmail",
      label: "Sync Gmail",
      description: "Poll ap@ inbox for new supplier invoices",
      fn: () => apiFetch("/api/gmail-poll?manual=1").then(r => r.json()),
    },
  ];

  const jobberOk = jobberStatus?.connected && !jobberStatus?.expired;
  const gmailOk = gmailStatus?.status === "connected";
  const qbOk = qbStatus?.status === "connected";

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'IBM Plex Sans', 'Roboto', sans-serif" }}>
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "16px 20px" }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Admin &amp; Diagnostics</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Sync controls and service connections — admin only</div>
      </div>

      <div style={{ padding: "24px 20px", maxWidth: 860 }}>
        {/* Connection Status */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 12, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>
            Connection Status
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <StatusCard
              label="Jobber"
              ok={jobberStatus == null ? null : jobberOk}
              detail={jobberStatus?.expires_at
                ? `Expires ${new Date(jobberStatus.expires_at).toLocaleString("en-CA", { dateStyle: "short", timeStyle: "short" })}`
                : null}
            />
            <StatusCard
              label="Gmail"
              ok={gmailStatus == null ? null : gmailOk}
              detail={gmailStatus?.email || null}
            />
            <StatusCard
              label="QuickBooks"
              ok={qbStatus == null ? null : qbOk}
              detail={
                qbStatus?.status === "token_revoked"
                  ? `Token revoked (${qbStatus?.health?.error || "invalid_grant"}) — click Reconnect`
                  : qbStatus?.health?.consecutive_failures > 0
                    ? `${qbStatus.health.consecutive_failures} consecutive health probe failures (${qbStatus.health.error || "unknown"})`
                    : qbStatus?.health?.last_success
                      ? `Last probe ${new Date(qbStatus.health.last_success).toLocaleString("en-CA", { dateStyle: "short", timeStyle: "short" })}`
                      : qbStatus?.company_name || null
              }
            />
            {qbStatus && !qbOk && qbStatus.status !== "not_connected" && (
              <div style={{ gridColumn: "1 / -1", marginTop: 8, padding: "10px 14px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8, fontSize: 13, color: "#f87171" }}>
                <strong>QuickBooks connection is down.</strong> Click&nbsp;
                <a
                  href="/api/quickbooks-auth?action=connect"
                  style={{ color: "#a5ce3a", textDecoration: "underline", fontWeight: 600 }}
                >
                  Reconnect QuickBooks
                </a>
                &nbsp;to re-authorize. Until this is done, no invoices will push (auto or manual).
              </div>
            )}
          </div>
          <button
            onClick={checkStatuses}
            style={{
              marginTop: 12, padding: "6px 12px", background: "transparent",
              border: `1px solid ${C.border}`, borderRadius: 6, color: C.muted,
              fontSize: 12, cursor: "pointer",
            }}
          >
            ↻ Refresh Status
          </button>
        </div>

        {/* Sync Controls */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 12, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>
            Manual Sync
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {syncs.map(s => (
              <SyncRow
                key={s.key}
                label={s.label}
                description={s.description}
                loading={!!syncing[s.key]}
                result={results[s.key]}
                onSync={() => runSync(s.key, s.fn)}
              />
            ))}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 16 }}>
            Most syncs run automatically via daily cron. Use manual sync only for troubleshooting or backfill.
          </div>
        </div>

        {/* QuickBooks Auto-Push */}
        <div style={{ marginTop: 32 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 12, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>
            QuickBooks Auto-Push
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 18px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                  Auto-publish high-certainty supplier invoices
                </div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.55 }}>
                  When ON, invoices polled from <code style={{ color: C.text, fontSize: 11 }}>ap@</code> are
                  pushed to QuickBooks as Bills automatically — but only when ALL of these are true:
                </div>
                <ul style={{ margin: "8px 0 0 18px", padding: 0, color: C.muted, fontSize: 12, lineHeight: 1.7 }}>
                  <li>Supplier matches a known wholesaler / fuel card / permit issuer</li>
                  <li>QB account auto-assigned by category rules</li>
                  <li>Vendor already exists in QuickBooks (no auto-create)</li>
                  <li>Has an invoice number, total &gt; $0, and not a credit memo</li>
                </ul>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.55 }}>
                  Anything that fails a check stays in Expenses for manual review. Status syncs both ways
                  with the QB Audit page. Every push (manual or auto) first queries QuickBooks for an
                  existing Bill with the same vendor + invoice number — if one is found, we link to it
                  instead of creating a duplicate.
                </div>
              </div>
              <button
                onClick={() => toggleAutoPush(!autoPushEnabled)}
                disabled={autoPushSaving}
                role="switch"
                aria-checked={autoPushEnabled}
                title={autoPushEnabled ? "Click to disable auto-push" : "Click to enable auto-push"}
                style={{
                  width: 56, height: 30, borderRadius: 15,
                  border: `1px solid ${autoPushEnabled ? C.green : C.border}`,
                  background: autoPushEnabled ? C.green : "#1a1d23",
                  cursor: autoPushSaving ? "wait" : "pointer",
                  position: "relative", flexShrink: 0,
                  opacity: autoPushSaving ? 0.6 : 1,
                  transition: "background 0.15s, border-color 0.15s",
                }}
              >
                <div style={{
                  position: "absolute", top: 2, left: autoPushEnabled ? 28 : 2,
                  width: 24, height: 24, borderRadius: "50%",
                  background: autoPushEnabled ? "#0d0f12" : "#8a919c",
                  transition: "left 0.15s",
                }} />
              </button>
            </div>
            <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 6, fontSize: 11, fontWeight: 600,
              background: autoPushEnabled ? C.greenDim : "rgba(138,145,156,0.08)",
              color: autoPushEnabled ? C.green : C.muted,
              border: `1px solid ${autoPushEnabled ? "rgba(34,197,94,0.2)" : C.border}`,
            }}>
              {autoPushEnabled ? "● Auto-push is ON" : "○ Auto-push is OFF"}
            </div>
            {autoPushMsg && (
              <div style={{
                marginTop: 10, padding: "8px 12px", borderRadius: 6, fontSize: 12,
                background: autoPushMsg.ok ? C.greenDim : C.redDim,
                color: autoPushMsg.ok ? C.green : C.red,
                border: `1px solid ${autoPushMsg.ok ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"}`,
              }}>
                {autoPushMsg.text}
              </div>
            )}
          </div>
        </div>

        {/* QuickBooks cache freshness — qb_vendors / qb_accounts mirrors */}
        {(() => {
          const ageMs = qbCacheStatus?.last_synced_at
            ? Date.now() - new Date(qbCacheStatus.last_synced_at).getTime()
            : null;
          const ageHours = ageMs != null ? Math.round(ageMs / 3600000) : null;
          const stale = ageHours != null && ageHours >= 24;
          const veryStale = ageHours != null && ageHours >= 72;
          const tone = veryStale ? "red" : stale ? "amber" : "green";
          const accent = tone === "red" ? C.red : tone === "amber" ? "#f59e0b" : C.green;
          const accentBg = tone === "red" ? C.redDim : tone === "amber" ? "rgba(245,158,11,0.10)" : C.greenDim;
          return (
            <div style={{ marginTop: 32 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 12, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                QuickBooks Cache
              </div>
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Vendor &amp; Account mirror</div>
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                      Auto-push checks <code>qb_vendors</code> before sending a Bill to QuickBooks.
                      If a supplier exists in QB but is missing from this cache, every push for that
                      supplier silently aborts. Resync if you've added vendors / accounts in QB recently.
                    </div>
                    <div style={{ marginTop: 10, fontSize: 12, color: "#e0e0e0" }}>
                      {qbCacheStatus == null ? (
                        <span style={{ color: C.muted }}>Loading…</span>
                      ) : (
                        <>
                          <strong>{qbCacheStatus.vendors}</strong> vendor{qbCacheStatus.vendors === 1 ? "" : "s"}
                          {" · "}
                          <strong>{qbCacheStatus.accounts}</strong> account{qbCacheStatus.accounts === 1 ? "" : "s"}
                          {qbCacheStatus.last_synced_at ? (
                            <> · last synced {ageHours}h ago ({new Date(qbCacheStatus.last_synced_at).toLocaleString("en-CA")})</>
                          ) : (
                            <> · <span style={{ color: C.red }}>never synced</span></>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={resyncQbCache}
                    disabled={qbCacheSyncing}
                    style={{
                      background:    accentBg,
                      color:         accent,
                      border:        `1px solid ${accent}40`,
                      borderRadius:  6,
                      padding:       "6px 14px",
                      fontSize:      12,
                      fontWeight:    600,
                      cursor:        qbCacheSyncing ? "wait" : "pointer",
                      flexShrink:    0,
                      fontFamily:    "inherit",
                    }}
                  >
                    {qbCacheSyncing ? "Syncing…" : "Resync"}
                  </button>
                </div>
                {(stale || veryStale) && (
                  <div style={{
                    marginTop: 10, padding: "8px 12px", borderRadius: 6, fontSize: 12,
                    background: accentBg, color: accent,
                    border: `1px solid ${accent}30`,
                  }}>
                    {veryStale
                      ? "⚠ Cache is more than 3 days old. New vendors / accounts in QuickBooks may not be auto-pushable."
                      : "Cache is more than 24h old. Resync if you've recently added vendors or accounts in QB."}
                  </div>
                )}
                {qbCacheMsg && (
                  <div style={{
                    marginTop: 10, padding: "8px 12px", borderRadius: 6, fontSize: 12,
                    background: qbCacheMsg.ok ? C.greenDim : C.redDim,
                    color: qbCacheMsg.ok ? C.green : C.red,
                    border: `1px solid ${qbCacheMsg.ok ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"}`,
                  }}>
                    {qbCacheMsg.text}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Claude API Spend */}
        <div style={{ marginTop: 32 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>
              Claude API Spend
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {[1, 7, 30].map(d => (
                <button
                  key={d}
                  onClick={() => { setSpendDays(d); loadClaudeSpend(d); }}
                  disabled={spendLoading}
                  style={{
                    padding: "5px 12px", borderRadius: 6,
                    border: `1px solid ${spendDays === d ? C.blue : C.border}`,
                    background: spendDays === d ? "rgba(96,165,250,0.1)" : "transparent",
                    color: spendDays === d ? C.blue : C.muted,
                    fontSize: 12, fontWeight: 600,
                    cursor: spendLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {d === 1 ? "Today" : `${d}d`}
                </button>
              ))}
              <button
                onClick={() => loadClaudeSpend(spendDays)}
                disabled={spendLoading}
                style={{
                  marginLeft: 4, padding: "5px 10px", borderRadius: 6,
                  border: `1px solid ${C.border}`, background: "transparent",
                  color: C.muted, fontSize: 12, cursor: spendLoading ? "not-allowed" : "pointer",
                }}
              >
                ↻
              </button>
            </div>
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
              Per-endpoint Anthropic spend, aggregated from <code style={{ color: C.text, fontSize: 11 }}>claude_call_log</code>.
              Cost estimates use list prices ($3/M in, $15/M out for Sonnet; $1/$5 for Haiku; cache reads at 0.1×).
              Status counts show <em>ok</em> vs everything else (retries, non-JSON, API errors).
            </div>
            {spendError && (
              <div style={{ padding: "8px 12px", borderRadius: 6, fontSize: 12, background: C.redDim, color: C.red, border: `1px solid rgba(239,68,68,0.2)`, marginBottom: 10 }}>
                {spendError}
              </div>
            )}
            {spendLoading && !spendData && (
              <div style={{ fontSize: 12, color: C.muted, padding: "8px 0" }}>Loading…</div>
            )}
            {spendData && (
              <>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
                  <div style={{ flex: "1 1 140px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px" }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Total estimated cost</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: C.green }}>
                      ${spendData.totals?.cost_usd?.toFixed(2) ?? "0.00"}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                      over {spendData.days} day{spendData.days === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div style={{ flex: "1 1 140px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px" }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Total calls</div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>
                      {(spendData.totals?.calls ?? 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                      {(spendData.total_rows ?? 0).toLocaleString()} log rows
                    </div>
                  </div>
                </div>
                {(!spendData.by_caller || spendData.by_caller.length === 0) ? (
                  <div style={{ fontSize: 12, color: C.muted, padding: "12px 0", textAlign: "center" }}>
                    No Claude calls logged yet. (Telemetry started landing with the May 12 deploy — give it some traffic.)
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${C.border}`, color: C.muted, textAlign: "left" }}>
                          <th style={{ padding: "8px 8px", fontWeight: 600 }}>Caller</th>
                          <th style={{ padding: "8px 8px", fontWeight: 600, textAlign: "right" }}>Calls</th>
                          <th style={{ padding: "8px 8px", fontWeight: 600, textAlign: "right" }}>Input</th>
                          <th style={{ padding: "8px 8px", fontWeight: 600, textAlign: "right" }}>Output</th>
                          <th style={{ padding: "8px 8px", fontWeight: 600, textAlign: "right" }}>Cached read</th>
                          <th style={{ padding: "8px 8px", fontWeight: 600, textAlign: "right" }}>Est. $</th>
                          <th style={{ padding: "8px 8px", fontWeight: 600, textAlign: "right" }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {spendData.by_caller.map((b, i) => {
                          const okRatio = b.calls > 0 ? b.status_ok / b.calls : 0;
                          const statusColor = okRatio === 1 ? C.green : okRatio >= 0.9 ? C.yellow : C.red;
                          return (
                            <tr key={b.caller} style={{ borderBottom: i === spendData.by_caller.length - 1 ? "none" : `1px solid ${C.border}` }}>
                              <td style={{ padding: "8px 8px", color: C.text, fontFamily: "monospace", fontSize: 11 }}>
                                {b.caller}
                                {b.models?.length > 0 && (
                                  <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{b.models.join(", ")}</div>
                                )}
                              </td>
                              <td style={{ padding: "8px 8px", textAlign: "right" }}>{b.calls.toLocaleString()}</td>
                              <td style={{ padding: "8px 8px", textAlign: "right", color: C.muted }}>{b.input.toLocaleString()}</td>
                              <td style={{ padding: "8px 8px", textAlign: "right", color: C.muted }}>{b.output.toLocaleString()}</td>
                              <td style={{ padding: "8px 8px", textAlign: "right", color: b.cache_read > 0 ? C.green : C.muted }}>
                                {b.cache_read.toLocaleString()}
                              </td>
                              <td style={{ padding: "8px 8px", textAlign: "right", fontWeight: 600, color: b.cost_usd > 1 ? C.yellow : C.text }}>
                                ${b.cost_usd.toFixed(2)}
                              </td>
                              <td style={{ padding: "8px 8px", textAlign: "right", color: statusColor, fontSize: 11 }}>
                                {b.status_ok}/{b.calls}
                                {b.status_other > 0 && <span style={{ color: C.red, marginLeft: 4 }}>({b.status_other} err)</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Month-end adjusting entries (WIP deferrals and the like) posted
            straight to QuickBooks. Admin-only, same as /api/quickbooks-journal. */}
        <PostJournalEntry />

        {/* Bulk-purchase cost split (Kal Tire #2621/#2622 and any future
            same-scope-twice pair). Admin-only surface, matching the
            admin-only /api/expense-split endpoint. */}
        <CostSplitGroups />

        {/* Credit Note Audit */}
        <div style={{ marginTop: 32 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>
              Credit Note Audit
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                onClick={() => { setUnflipMode(false); }}
                style={{
                  padding: "5px 10px", borderRadius: 6,
                  border: `1px solid ${!unflipMode ? C.blue : C.border}`,
                  background: !unflipMode ? "rgba(96,165,250,0.1)" : "transparent",
                  color: !unflipMode ? C.blue : C.muted,
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
              >Scan</button>
              <button
                onClick={() => { setUnflipMode(true); if (!unflipData) loadFlippedRows(); }}
                style={{
                  padding: "5px 10px", borderRadius: 6,
                  border: `1px solid ${unflipMode ? C.blue : C.border}`,
                  background: unflipMode ? "rgba(96,165,250,0.1)" : "transparent",
                  color: unflipMode ? C.blue : C.muted,
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
              >Undo Flips</button>
              {!unflipMode && (
                <>
                  <input
                    type="text"
                    value={creditAuditSupplier}
                    onChange={(e) => setCreditAuditSupplier(e.target.value)}
                    placeholder="supplier substring (e.g. city)"
                    style={{
                      padding: "5px 10px", borderRadius: 6,
                      border: `1px solid ${C.border}`, background: C.surface, color: C.text,
                      fontSize: 12, width: 200,
                    }}
                  />
                  <button
                    onClick={runCreditNoteAudit}
                    disabled={creditAuditLoading}
                    style={{
                      padding: "6px 14px", borderRadius: 6,
                      border: `1px solid ${C.border}`, background: "transparent",
                      color: C.muted, fontSize: 12, fontWeight: 600,
                      cursor: creditAuditLoading ? "not-allowed" : "pointer",
                    }}
                  >
                    {creditAuditLoading ? "Scanning…" : "Run Scan"}
                  </button>
                </>
              )}
              {unflipMode && (
                <button
                  onClick={loadFlippedRows}
                  disabled={unflipLoading}
                  style={{
                    padding: "6px 14px", borderRadius: 6,
                    border: `1px solid ${C.border}`, background: "transparent",
                    color: C.muted, fontSize: 12, fontWeight: 600,
                    cursor: unflipLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {unflipLoading ? "Loading…" : "Refresh"}
                </button>
              )}
            </div>
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
              Downloads each PDF receipt for the matched supplier rows that have a positive total + no credit-memo flag,
              parses the text, and flags any with a <strong>"Credit Note - Do Not Pay"</strong>, <strong>"Credit Memo"</strong>, or
              <strong> "Return Note"</strong> header. Suspect rows can be bulk-flipped to negative totals + is_credit_memo=true.
              Rows that were already pushed get an <code style={{ fontSize: 11, color: C.text }}>admin_notes</code> flag so
              you know a corrective Jobber/QB entry is still required.
            </div>
            {creditAuditError && (
              <div style={{ padding: "8px 12px", borderRadius: 6, fontSize: 12, background: creditAuditError.startsWith("Flipped") ? C.greenDim : C.redDim, color: creditAuditError.startsWith("Flipped") ? C.green : C.red, border: `1px solid ${creditAuditError.startsWith("Flipped") ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"}`, marginBottom: 10 }}>
                {creditAuditError}
              </div>
            )}
            {creditAuditLoading && !creditAuditData && (
              <div style={{ fontSize: 12, color: C.muted, padding: "8px 0" }}>Scanning PDFs… can take ~60s for 200 rows.</div>
            )}
            {!unflipMode && creditAuditData && (
              <>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
                  <div style={{ flex: "1 1 140px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px" }}>
                    <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Credit notes found</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: creditAuditData.credit_notes_found > 0 ? C.red : C.green }}>
                      {creditAuditData.credit_notes_found}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                      across {creditAuditData.scanned} scanned ({creditAuditData.total_candidates} candidates)
                    </div>
                  </div>
                  {creditAuditData.credit_notes_found > 0 && (
                    <div style={{ flex: "1 1 140px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px" }}>
                      <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Selected to flip</div>
                      <div style={{ fontSize: 22, fontWeight: 700 }}>
                        {creditAuditSelected.size}
                      </div>
                      <button
                        onClick={flipSelectedCreditNotes}
                        disabled={creditAuditFlipping || creditAuditSelected.size === 0}
                        style={{
                          marginTop: 6, padding: "5px 12px", borderRadius: 6,
                          border: `1px solid ${C.red}40`, background: C.redDim, color: C.red,
                          fontSize: 12, fontWeight: 600,
                          cursor: (creditAuditFlipping || creditAuditSelected.size === 0) ? "not-allowed" : "pointer",
                        }}
                      >
                        {creditAuditFlipping ? "Flipping…" : `🔄 Flip ${creditAuditSelected.size} to credit`}
                      </button>
                    </div>
                  )}
                </div>
                {(creditAuditData.results || []).filter((rr) => rr.isCreditNote).length === 0 ? (
                  <div style={{ fontSize: 12, color: C.green, padding: "12px 0", textAlign: "center" }}>
                    No credit notes found in the scanned rows. ✓
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${C.border}`, color: C.muted, textAlign: "left" }}>
                          <th style={{ padding: "8px 6px", width: 30 }}></th>
                          <th style={{ padding: "8px 6px", fontWeight: 600 }}>Invoice #</th>
                          <th style={{ padding: "8px 6px", fontWeight: 600 }}>Date</th>
                          <th style={{ padding: "8px 6px", fontWeight: 600 }}>Job #</th>
                          <th style={{ padding: "8px 6px", fontWeight: 600, textAlign: "right" }}>Total</th>
                          <th style={{ padding: "8px 6px", fontWeight: 600 }}>Pushed?</th>
                          <th style={{ padding: "8px 6px", fontWeight: 600 }}>Matched phrase</th>
                          <th style={{ padding: "8px 6px", fontWeight: 600 }}>PDF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(creditAuditData.results || [])
                          .filter((rr) => rr.isCreditNote)
                          .map((rr) => {
                            const pushed = !!rr.row.jobber_expense_id || !!rr.row.qb_bill_id;
                            return (
                              <tr key={rr.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                                <td style={{ padding: "8px 6px" }}>
                                  <input
                                    type="checkbox"
                                    checked={creditAuditSelected.has(rr.id)}
                                    onChange={() => toggleCreditAuditRow(rr.id)}
                                  />
                                </td>
                                <td style={{ padding: "8px 6px", fontFamily: "monospace", fontSize: 11 }}>
                                  {rr.row.invoice_number || "—"}
                                </td>
                                <td style={{ padding: "8px 6px", color: C.muted }}>{rr.row.invoice_date || "—"}</td>
                                <td style={{ padding: "8px 6px", color: C.muted }}>{rr.row.jobber_job_number || "—"}</td>
                                <td style={{ padding: "8px 6px", textAlign: "right", color: C.red, fontWeight: 600 }}>
                                  +${Number(rr.row.total).toFixed(2)}
                                </td>
                                <td style={{ padding: "8px 6px", color: pushed ? C.red : C.muted, fontSize: 11 }}>
                                  {pushed ? (
                                    <>
                                      {rr.row.jobber_expense_id ? "JB" : ""}
                                      {rr.row.jobber_expense_id && rr.row.qb_bill_id ? "+" : ""}
                                      {rr.row.qb_bill_id ? "QB" : ""}
                                    </>
                                  ) : "no"}
                                </td>
                                <td style={{ padding: "8px 6px", color: C.muted, fontSize: 11, fontStyle: "italic" }}>
                                  …{rr.context || rr.matched}…
                                </td>
                                <td style={{ padding: "8px 6px" }}>
                                  {rr.row.receipt_url ? (
                                    <a href={rr.row.receipt_url} target="_blank" rel="noopener noreferrer" style={{ color: C.blue, fontSize: 11 }}>
                                      open
                                    </a>
                                  ) : "—"}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* Undo Flips view */}
            {unflipMode && (
              <>
                {unflipLoading && !unflipData && (
                  <div style={{ fontSize: 12, color: C.muted, padding: "8px 0" }}>Loading flipped rows…</div>
                )}
                {unflipData && (
                  <>
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
                      <div style={{ flex: "1 1 140px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px" }}>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Flipped by audit</div>
                        <div style={{ fontSize: 22, fontWeight: 700 }}>{unflipData.count}</div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                          rows marked as credit memo via this audit
                        </div>
                      </div>
                      {unflipSelected.size > 0 && (
                        <div style={{ flex: "1 1 140px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px" }}>
                          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Selected to un-flip</div>
                          <div style={{ fontSize: 22, fontWeight: 700 }}>{unflipSelected.size}</div>
                          <button
                            onClick={unflipSelectedRows}
                            disabled={unflipRunning}
                            style={{
                              marginTop: 6, padding: "5px 12px", borderRadius: 6,
                              border: `1px solid ${C.green}40`, background: C.greenDim, color: C.green,
                              fontSize: 12, fontWeight: 600,
                              cursor: unflipRunning ? "not-allowed" : "pointer",
                            }}
                          >
                            {unflipRunning ? "Un-flipping…" : `↩ Un-flip ${unflipSelected.size}`}
                          </button>
                        </div>
                      )}
                    </div>
                    {unflipData.rows.length === 0 ? (
                      <div style={{ fontSize: 12, color: C.muted, padding: "12px 0", textAlign: "center" }}>
                        No audit-flipped rows yet.
                      </div>
                    ) : (
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr style={{ borderBottom: `1px solid ${C.border}`, color: C.muted, textAlign: "left" }}>
                              <th style={{ padding: "8px 6px", width: 30 }}></th>
                              <th style={{ padding: "8px 6px", fontWeight: 600 }}>Supplier</th>
                              <th style={{ padding: "8px 6px", fontWeight: 600 }}>Invoice #</th>
                              <th style={{ padding: "8px 6px", fontWeight: 600 }}>Date</th>
                              <th style={{ padding: "8px 6px", fontWeight: 600 }}>Job #</th>
                              <th style={{ padding: "8px 6px", fontWeight: 600, textAlign: "right" }}>Total</th>
                              <th style={{ padding: "8px 6px", fontWeight: 600 }}>Pushed?</th>
                              <th style={{ padding: "8px 6px", fontWeight: 600 }}>Note</th>
                              <th style={{ padding: "8px 6px", fontWeight: 600 }}>PDF</th>
                            </tr>
                          </thead>
                          <tbody>
                            {unflipData.rows.map((rr) => {
                              const pushed = !!rr.jobber_expense_id || !!rr.qb_bill_id;
                              return (
                                <tr key={rr.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                                  <td style={{ padding: "8px 6px" }}>
                                    <input
                                      type="checkbox"
                                      checked={unflipSelected.has(rr.id)}
                                      onChange={() => toggleUnflipRow(rr.id)}
                                    />
                                  </td>
                                  <td style={{ padding: "8px 6px", fontSize: 11 }}>{rr.supplier || "—"}</td>
                                  <td style={{ padding: "8px 6px", fontFamily: "monospace", fontSize: 11 }}>{rr.invoice_number || "—"}</td>
                                  <td style={{ padding: "8px 6px", color: C.muted }}>{rr.invoice_date || "—"}</td>
                                  <td style={{ padding: "8px 6px", color: C.muted }}>{rr.jobber_job_number || "—"}</td>
                                  <td style={{ padding: "8px 6px", textAlign: "right", color: Number(rr.total) < 0 ? C.green : C.text, fontWeight: 600 }}>
                                    {Number(rr.total) < 0 ? "" : "+"}${Number(rr.total).toFixed(2)}
                                  </td>
                                  <td style={{ padding: "8px 6px", color: pushed ? C.red : C.muted, fontSize: 11 }}>
                                    {pushed ? (
                                      <>
                                        {rr.jobber_expense_id ? "JB" : ""}
                                        {rr.jobber_expense_id && rr.qb_bill_id ? "+" : ""}
                                        {rr.qb_bill_id ? "QB" : ""}
                                      </>
                                    ) : "no"}
                                  </td>
                                  <td style={{ padding: "8px 6px", color: C.muted, fontSize: 11, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={rr.admin_notes}>
                                    {rr.admin_notes ? rr.admin_notes.split("\n").pop() : "—"}
                                  </td>
                                  <td style={{ padding: "8px 6px" }}>
                                    {rr.receipt_url ? (
                                      <a href={rr.receipt_url} target="_blank" rel="noopener noreferrer" style={{ color: C.blue, fontSize: 11 }}>
                                        open
                                      </a>
                                    ) : "—"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Profit Bonus settings */}
        <div style={{ marginTop: 32 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 12, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>
            Profit Bonus
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
              Lead tech and crew get separate bonus % of the excess profit (everything above the margin threshold).
              Crew pool is split equally among everyone who logged hours on the job (plus the admin), unless you set
              per-job weights in Bonus Tracker. Threshold changes apply to all <em>unpaid</em> bonuses.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {BONUS_KEYS.map(meta => {
                const val = bonusSettings[meta.key] ?? "";
                const saved = bonusSettingsSaved[meta.key];
                const saving = !!bonusSettingsSaving[meta.key];
                const msg = bonusSettingsMsg[meta.key];
                const dirty = String(val) !== String(saved);
                return (
                  <div key={meta.key} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px" }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{meta.label}</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2, lineHeight: 1.45, minHeight: 32 }}>{meta.hint}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                      {meta.suffix === "$" && <span style={{ fontSize: 13, color: C.muted }}>$</span>}
                      <input
                        type="number"
                        value={val}
                        onChange={e => setBonusSettings(s => ({ ...s, [meta.key]: e.target.value }))}
                        min={meta.min} max={meta.max} step={meta.step}
                        style={{
                          width: 90, padding: "6px 10px", borderRadius: 6,
                          border: `1px solid ${C.border}`, background: C.surface, color: C.text,
                          fontSize: 13,
                        }}
                      />
                      {meta.suffix === "%" && <span style={{ fontSize: 13, color: C.muted }}>%</span>}
                      <button
                        onClick={() => saveBonusSetting(meta.key)}
                        disabled={saving || !dirty}
                        style={{
                          marginLeft: "auto", padding: "6px 14px", borderRadius: 6,
                          border: `1px solid ${C.border}`, background: "transparent",
                          color: !dirty ? C.muted : C.green,
                          fontSize: 12, fontWeight: 600,
                          cursor: (saving || !dirty) ? "not-allowed" : "pointer",
                          opacity: saving ? 0.5 : 1,
                        }}
                      >
                        {saving ? "Saving…" : "Save"}
                      </button>
                    </div>
                    {msg && (
                      <div style={{
                        marginTop: 8, padding: "6px 10px", borderRadius: 5, fontSize: 11,
                        background: msg.ok ? C.greenDim : C.redDim,
                        color: msg.ok ? C.green : C.red,
                      }}>
                        {msg.text}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
